const generateProductSlipImage = async (imageUrl, measurements, configuration) => {
    if (!configuration) {
        throw new Error("Configuration not found in PatioContext.");
    }


    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = imageUrl;

    await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
    });

    let { naturalWidth, naturalHeight } = image;
    const maxDimension = 1024;
    const aspectRatio = naturalWidth / naturalHeight;

    if (naturalWidth > maxDimension || naturalHeight > maxDimension) {
        if (naturalWidth > naturalHeight) {
            naturalWidth = maxDimension;
            naturalHeight = Math.round(maxDimension / aspectRatio);
        } else {
            naturalHeight = maxDimension;
            naturalWidth = Math.round(maxDimension * aspectRatio);
        }
    }

    const canvas = document.createElement("canvas");
    canvas.width = naturalWidth;
    canvas.height = naturalHeight;
    const ctx = canvas.getContext("2d");

    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    const scale = (value) => value * (canvas.width / 1024);

    const drawShape = (shape, scaleX, scaleY, options = {}) => {
        const {
            radius = 0, width = 0, height = 0, fontSize = 0, text = "",
            fillColor = "#000", textColor = "#fff", textAlign = "center", textBaseline = "middle",
        } = options;

        ctx.font = `${fontSize}px Arial`;
        ctx.textAlign = textAlign;
        ctx.textBaseline = textBaseline;

        ctx.save();
        ctx.beginPath();
        if (shape === "circle") {
            ctx.arc(scaleX, scaleY, radius, 0, Math.PI * 2);
        } else if (shape === "rect") {
            ctx.roundRect(scaleX - width / 2, scaleY - height / 2, width, height, radius);
        }
        ctx.fillStyle = fillColor;
        ctx.fill();
        ctx.restore();

        if (text) {
            ctx.save();
            ctx.fillStyle = textColor;
            ctx.fillText(text, scaleX, scaleY);
            ctx.restore();
        }
    };

    const drawMeasurementLine = (context, measurement) => {
        if (!measurement) return;
        const { point_one_x, point_one_y, point_two_x, point_two_y } = measurement.line_points;

        // Convert string percentages to actual pixel coordinates on the canvas
        const x1 = parseFloat(point_one_x) * context.canvas.width;
        const y1 = parseFloat(point_one_y) * context.canvas.height;
        const x2 = parseFloat(point_two_x) * context.canvas.width;
        const y2 = parseFloat(point_two_y) * context.canvas.height;

        context.save();


        // the secondary (gapped) dashed line
        context.beginPath();
        context.moveTo(x1, y1);
        context.lineTo(x2, y2);
        context.strokeStyle = '#fff3';
        context.lineWidth = 3;
        context.setLineDash([10, 10]);
        context.lineDashOffset = 0;
        context.stroke();

        // the main dashed line
        context.beginPath();
        context.moveTo(x1, y1);
        context.lineTo(x2, y2);
        context.strokeStyle = '#4443';
        context.lineWidth = 3;
        context.setLineDash([10, 10]);
        context.lineDashOffset = 10;
        context.stroke();



        // LINES AT THE ENDS OF THE LINE

        const dx = x2 - x1;
        const dy = y2 - y1;

        const length = Math.sqrt(dx * dx + dy * dy);

        const endLineLength = 15;

        const nx = -(dy / length) * endLineLength;
        const ny = (dx / length) * endLineLength;

        // Draw first end line centered at x1
        context.beginPath();
        context.moveTo(x1 - nx / 2, y1 - ny / 2);
        context.lineTo(x1 + nx / 2, y1 + ny / 2);
        context.strokeStyle = '#4443';
        context.lineWidth = 2;
        context.setLineDash([]);
        context.stroke();

        // Draw second end line centered at x2
        context.beginPath();
        context.moveTo(x2 - nx / 2, y2 - ny / 2);
        context.lineTo(x2 + nx / 2, y2 + ny / 2);
        context.strokeStyle = '#4443';
        context.lineWidth = 2;
        context.setLineDash([]);
        context.stroke();

        context.restore();
    };

    const drawCurvedMeasurementLine = (context, measurement) => {
        if (!measurement) return;
        const { point_one_x, point_one_y, point_two_x, point_two_y, curve_start_x, curve_start_y, curve_end_x, curve_end_y } = measurement.line_points;

        // Convert string percentages to actual pixel coordinates on the canvas
        const x1 = parseFloat(point_one_x) * context.canvas.width;
        const y1 = parseFloat(point_one_y) * context.canvas.height;
        const x2 = parseFloat(point_two_x) * context.canvas.width;
        const y2 = parseFloat(point_two_y) * context.canvas.height;
        // cp means control point, for cubic bezier curve
        const cp1x = parseFloat(curve_start_x) * context.canvas.width;
        const cp1y = parseFloat(curve_start_y) * context.canvas.height;
        const cp2x = parseFloat(curve_end_x) * context.canvas.width;
        const cp2y = parseFloat(curve_end_y) * context.canvas.height;

        context.save();

        context.beginPath();
        context.moveTo(x1, y1);
        context.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x2, y2);
        context.strokeStyle = '#fff3';
        context.lineWidth = 3;
        context.setLineDash([10, 10]);
        context.lineDashOffset = 10;
        context.stroke();

        context.beginPath();
        context.moveTo(x1, y1);
        context.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x2, y2);
        context.strokeStyle = '#4443';
        context.lineWidth = 3;
        context.setLineDash([10, 10]);
        context.lineDashOffset = 0;
        context.stroke();

        context.restore();
    };

    measurements.forEach((measurement, index) => {
        const { x, y, key } = measurement;
        if (!x || !y) return;
        const scaledX = x * canvas.width;
        const scaledY = y * canvas.height;
        const dot = String.fromCharCode(65 + index);
        const tooltip = configuration[key] || "N/A";

        // Dot
        const dotFontSize = scale(36);
        const dotPadding = scale(8);
        const dotRadius = (dotFontSize + dotPadding * 2) / 2;
        drawShape("circle", scaledX, scaledY, {
            radius: dotRadius,
            fillColor: "#00344e",
            text: dot,
            textColor: "#ffcb04",
            fontSize: dotFontSize,
        });


        // line_points 
        if (measurement.line_points?.isCurved) {
            drawCurvedMeasurementLine(ctx, measurement);
        } else {
            drawMeasurementLine(ctx, measurement);
        }

        // Tooltip
        const tooltipFontSize = scale(28);
        const tooltipPadding = scale(6);
        const tooltipWidth = ctx.measureText(tooltip).width + tooltipPadding * 2 + scale(8);
        const tooltipHeight = tooltipFontSize + tooltipPadding * 2;
        const tooltipRadius = scale(6);
        const tooltipY = scaledY - dotRadius * 2 - scale(5);
        drawShape("rect", scaledX, tooltipY, {
            width: tooltipWidth,
            height: tooltipHeight,
            radius: tooltipRadius,
            fillColor: "#00344e",
            text: `${tooltip}"`,
            textColor: "#ffcb04",
            fontSize: tooltipFontSize,
        });

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(scaledX - (tooltipWidth / 2) / 2, tooltipY + tooltipHeight / 2);
        ctx.lineTo(scaledX + (tooltipWidth / 2) / 2, tooltipY + tooltipHeight / 2);
        ctx.lineTo(scaledX, scaledY - dotRadius);
        ctx.fillStyle = "#00344e";
        ctx.fill();
        ctx.restore();
    });

    return canvas.toDataURL("image/png");
};

export default generateProductSlipImage;
